import { typedAction } from "../helpers";
import { SharepointInfoState } from "../models/reduxModels";
import { RESET_SHAREPOINT_INFO, SET_SHAREPOINT_INFO, SET_IS_SITES_LOADING, SET_SITES_LAST_CACHE_REFRESH } from "./actionTypes";
import authModule from "../../../../utilities/AuthModule";
import { getAllSubSites, searchSharePointSites } from "../../services/GraphService";
import { ThunkAction } from "redux-thunk";
import { RootState } from "..";
import { Action } from "redux";
import { decrementAmountOfLoaders, incrementAmountOfLoaders } from "./loader";
import { logger } from "../../../../utilities/logger";
import { IHubSite } from "../../interfaces/IHubSite";
import { getHubSites, getSharepointListItems } from "../../services/SharePointRestServices";
import { getInactiveClientSites } from "../../services/inactiveClientSites";
import { SharePointSiteHelper } from "../../../../utilities/SharePointSiteHelper";
import { ISiteInfo, ISiteSearchRequest, ISiteSearchResult, filterNonTopLevelISiteInfoArray } from "../../interfaces/ISiteInfo";
import { IGraphSearchPager } from "../../interfaces/IGraphSearchPager";
import { getLocalStorage, localStorageKeys, setLocalStorage } from "../../components/helpers/localStorageHelper";
import { IManagedSite, IManagedSiteCollection } from "../../interfaces/IManagedSite";
import { docubirdManagedSitesDatasource } from "../../interfaces/ITenantInfo";
import { apiGetProvisionedSites } from "../../services/ApiService";
import { dedupeSitesByIdentity } from "./sharepointInfoHelpers";

const initialState: SharepointInfoState = { mainUrl: "", isSitesLoading: false, sites: null, pageData: null };

const setIsSitesLoading = (isSitesLoading: boolean) => {
  return typedAction(SET_IS_SITES_LOADING, isSitesLoading);
}

export const setSharepointInfo = (info: SharepointInfoState) => {
  return typedAction(SET_SHAREPOINT_INFO, info);
};

const setSitesLastCacheRefresh = () => {
  return typedAction(SET_SITES_LAST_CACHE_REFRESH);
}

export const resetSharepointInfo = () => {
  return typedAction(RESET_SHAREPOINT_INFO);
}

export const getSites = (allowedSiteCollections?: string[], previousPageData?: IGraphSearchPager, skipLoaders: boolean = false, useCachedSites: boolean = false): ThunkAction<Promise<ISiteInfo[]>, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  if (useCachedSites) {
    const cachedSites = getLocalStorage(localStorageKeys.sites);
    if (Array.isArray(cachedSites) && cachedSites?.length > 0) {
      dispatch(
        setSharepointInfo({
          isSitesLoading: false,
          mainUrl: await getBaseSiteUrl(),
          sites: cachedSites,
          lastCacheFetch: new Date().getTime()
        })
      );
      dispatch(decrementAmountOfLoaders());
    }
    else {
      dispatch(setSitesLastCacheRefresh());
    }
  }

  let currentState = getState();
  let sites: ISiteInfo[] = currentState.sharePointInfo.sites ?? [];

  if (!currentState.sharePointInfo.isSitesLoading) {

    if (!skipLoaders) {
      if (!previousPageData?.moreResultsAvailable) dispatch(incrementAmountOfLoaders());
      dispatch(setIsSitesLoading(true));
    }

    let siteCollections: ISiteSearchResult = await getAllSites(
      allowedSiteCollections ?? currentState.tenantInfo.allowedSiteCollections,
      previousPageData,
      currentState.tenantInfo.enableProvisionedSiteRegistration,
      currentState.tenantInfo.docubirdManagedSitesDatasources,
      currentState.tenantInfo.clientListUrl
    );

    if (previousPageData?.moreResultsAvailable) {
      siteCollections.sites = siteCollections?.sites?.length > 0
        ? (currentState.sharePointInfo.sites?.filter(existingSite => !siteCollections.sites.some(newSite => existingSite.id === newSite.id)) ?? []).concat(siteCollections.sites)
        : currentState.sharePointInfo.sites;

      siteCollections.sites = dedupeSitesByIdentity(siteCollections.sites);
    }

    setLocalStorage(localStorageKeys.sites, siteCollections.sites);

    dispatch(
      setSharepointInfo({
        isSitesLoading: false,
        mainUrl: await getBaseSiteUrl(),
        sites: siteCollections.sites,
        pageData: siteCollections?.pageData,
        lastCacheFetch: currentState.sharePointInfo.lastCacheFetch,
        lastServerFetch: new Date().getTime(),
      })
    );

    if (!skipLoaders && !previousPageData?.moreResultsAvailable) dispatch(decrementAmountOfLoaders());

    sites = siteCollections.sites;
  }

  return sites;
};

export const getAllSites = async (allowedSiteCollections?: string[], pageData?: IGraphSearchPager, enableProvisionedSiteRegistration?: boolean, managedSitesDatasources?: docubirdManagedSitesDatasource[], clientListUrl?: string): Promise<ISiteSearchResult> => {

  const isNextPageRequest: boolean = pageData?.moreResultsAvailable;

  let [allHubSites, allowedHubSites, managedSites, provisionedSites, inactiveClientSites] = await Promise.all([
    !isNextPageRequest && getAllHubSites(),
    !isNextPageRequest && searchSharePointSites({ hubSitesOnly: true, allowedSites: allowedSiteCollections }),
    !isNextPageRequest && getDocubirdManagedSites(managedSitesDatasources),
    enableProvisionedSiteRegistration && !isNextPageRequest && apiGetProvisionedSites((await authModule.getAccount())?.accountId),
    !isNextPageRequest && clientListUrl?.trim()?.length > 0 && getInactiveClientSites(clientListUrl)
  ]);

  if (allHubSites?.length > 0 && allowedHubSites?.sites?.length > 0) {
    SharePointSiteHelper.clearHubs();

    allowedHubSites.sites.forEach(site => {
      site.isHub = true;

      const hub: IHubSite = allHubSites.find(hubSite => hubSite.siteUrl?.trim().toLowerCase() === site.webUrl?.trim().toLowerCase());
      if (hub) {
        site.hubId = hub.id;
        if (hub.title) site.name = hub.title;
        if (hub.description) site.description = hub.description;
        if (hub.parentHubSiteId && hub.parentHubSiteId !== "00000000-0000-0000-0000-000000000000") {
          const parentHub: IHubSite = allHubSites.find(hubSite => hubSite.id === hub.parentHubSiteId);
          if (parentHub && allowedHubSites.sites.findIndex(allowedHub => parentHub.siteUrl?.trim().toLowerCase() === allowedHub.webUrl?.trim().toLowerCase()) !== -1) {
            site.parentHubId = hub.parentHubSiteId;
          }
        }
      }
      else {
        const siteIdParts: string[] = site.id?.split(",") // xblox.sharepoint.com,058798cc-59c1-451f-8957-49e32c49aeb1,4c5fa046-b695-4b4b-86ef-b47bb86f7cbe
        if (Array.isArray(siteIdParts) && siteIdParts.length > 1 && siteIdParts[1]) {
          site.hubId = siteIdParts[1];
        }
      }

      if (site.hubId) {
        SharePointSiteHelper.hubSites.push(site);
      }
    });

  }

  if (Array.isArray(managedSites?.sites) && managedSites.sites.length > 0) {
    SharePointSiteHelper.managedSites = managedSites;
  }

  if (Array.isArray(provisionedSites) && provisionedSites.length > 0) {
    SharePointSiteHelper.provisionedSites = provisionedSites;
  }

  // Refresh the global inactive-client set on every full (first-page) load so it clears when the
  // client list is emptied/disabled or a fetch fails; leave it untouched during pagination.
  if (!isNextPageRequest) {
    SharePointSiteHelper.inactiveClientSites = Array.isArray(inactiveClientSites) ? inactiveClientSites as IManagedSite[] : [];
    SharePointSiteHelper.inactiveClientSitesLoaded = true;
  }

  const searchRequest: ISiteSearchRequest = {
    from: pageData?.from,
    allowedSites: allowedSiteCollections,
    hubSiteIds: SharePointSiteHelper.hubSites.map(hub => hub.hubId),
    pageLimit: Array.isArray(managedSites?.sites) && managedSites.sites.length > 100 ? 5 : 1
  };

  let siteCollections: ISiteSearchResult;
  let hubSiteChildren: ISiteSearchResult;
  let subSites: ISiteInfo[];
  [siteCollections, hubSiteChildren, subSites] = await Promise.all([
    searchSharePointSites(searchRequest),
    !isNextPageRequest && SharePointSiteHelper.hubSites.length > 0 && searchSharePointSites({ ...searchRequest, hubChildrenOnly: true, pageLimit: 5 }),
    !isNextPageRequest && getAllSubSites(searchRequest)
  ]);

  if (!isNextPageRequest) {
    if (SharePointSiteHelper.hubSites.length > 0) {
      siteCollections.sites = SharePointSiteHelper.hubSites
        .concat(
          (siteCollections?.sites ?? []).concat(hubSiteChildren?.sites ?? [])
            .filter(site => !allHubSites.some(hubSite => hubSite.siteUrl?.trim().toLowerCase() === site.webUrl?.trim().toLowerCase()))
        );
      // .filter(site => !site.parentHubId);
    }

    if (Array.isArray(subSites) && subSites.length > 0) {
      siteCollections.sites = siteCollections.sites
        .concat(subSites)
        .filter(filterNonTopLevelISiteInfoArray);
    }
  }
  else if (SharePointSiteHelper.hubSites.length > 0) {
    siteCollections.sites = siteCollections.sites
      .filter(site => !SharePointSiteHelper.hubSites.some(hubSite => hubSite.webUrl?.trim().toLowerCase() === site.webUrl?.trim().toLowerCase()));
  }

  siteCollections.sites = dedupeSitesByIdentity(siteCollections?.sites);
  SharePointSiteHelper.configureManagesSites(siteCollections?.sites);
  SharePointSiteHelper.configureInactiveSites(siteCollections?.sites);

  return siteCollections;
};

const getAllHubSites = async (): Promise<IHubSite[]> => {
  let hubSites: IHubSite[];
  try {
    const sharePointAccessToken: string = await authModule.getSharePointAccessToken();
    hubSites = await getHubSites(sharePointAccessToken, await getBaseSiteUrl());
  } catch (error) {
    logger(error, "sharePointInfo.ts (getAllHubSites)");
  }
  return hubSites?.length > 0 ? hubSites : [];
};

const getBaseSiteUrl = async (): Promise<string> => {
  return (await authModule.getAccount(false))?.sharePointBaseUrl;
};

const getDocubirdManagedSites = async (managedSitesDatasources: docubirdManagedSitesDatasource[]): Promise<IManagedSiteCollection> => {
  const managedSiteCollection: IManagedSiteCollection = {
    groups: new Set<string>(),
    sites: []
  }

  if (Array.isArray(managedSitesDatasources) && managedSitesDatasources.length > 0) {
    await Promise.all(managedSitesDatasources
      .filter(ds => ds.siteUrl && ds.listName)
      .map(async ds => {
        try {
          const managedSites: IManagedSiteCollection = await getSharepointListItems(ds.siteUrl, ds.listName, ds.groupField);
          if (Array.isArray(managedSites?.sites)) {
            for (const managedSite of managedSites.sites) {
              managedSiteCollection.sites.push(managedSite);
            }
            if (managedSites?.groups?.size > 0) {
              managedSites.groups.forEach(group => {
                managedSiteCollection.groups.add(group);
              });
            }
          }
        } catch (error) {
          logger(error, "SharePointInfo.ts (getDocubirdManagedSites)");
        }
      }));
  }

  return managedSiteCollection;
}

type SharepointInfoAction = ReturnType<typeof setSharepointInfo | typeof setSitesLastCacheRefresh | typeof resetSharepointInfo | typeof setIsSitesLoading>;

export function sharePointInfoReducer(state = initialState, action: SharepointInfoAction): SharepointInfoState {
  switch (action.type) {
    case SET_SHAREPOINT_INFO:
      return action.payload;
    case SET_SITES_LAST_CACHE_REFRESH:
      return { ...state, lastCacheFetch: new Date().getTime() };
    case SET_IS_SITES_LOADING:
      return { ...state, isSitesLoading: action.payload };
    case RESET_SHAREPOINT_INFO:
      return initialState;
    default:
      return state;
  }
}
